import { Map, Record } from "immutable";

import { IUser, User, AuthNormalisedEntities } from "@auth/models";
import { IFile, File } from "@files/models";
import { TranslatableMap } from "@localisation/models";
import { IAlbum, Album, MusicNormalisedEntities } from "@music/models";
import {
    RestModel,
    RestRecord,
    InnerStateWithPagination,
    NormalisedEntities,
} from "@orm/models";

export interface UploadInternalState extends InnerStateWithPagination {
    entities: UploadNormalisedEntities;
    list: number[];
}

export interface UploadState {
    upload: UploadInternalState;
}

interface ErrorMap {
    [key: string]: string;
}

export interface IBundle extends RestModel {
    id?: number;
    attrs?: IAlbum;
    errors?: ErrorMap;
    statusName?: string;
    metadataFileId?: number;
    bundleFileId?: number;
    albumId?: number;
    userId?: number;
    organisationId?: number;
    createdAt?: Date;
    updatedAt?: Date;
    status?: IBundleStatus;
    metadataFile?: IFile;
    bundleFile?: IFile;
    album?: IAlbum;
    user?: IUser;
}

export interface IBundleStatus {
    name?: string;
    label?: TranslatableMap;
}

const BundleRecord = RestRecord({
    id: undefined,
    attrs: undefined,
    errors: undefined,
    statusName: undefined,
    metadataFileId: undefined,
    bundleFileId: undefined,
    albumId: undefined,
    userId: undefined,
    organisationId: undefined,
    createdAt: undefined,
    updatedAt: undefined,
    status: undefined,
    metadataFile: undefined,
    bundleFile: undefined,
    album: undefined,
    user: undefined,
});

const BundleStatusRecord = Record({
    name:  undefined,
    label: undefined,
});

export class Bundle extends BundleRecord implements IBundle {
    public static RELATIONS = [
        "status",
        "metadataFile",
        "bundleFile",
        "album",
        "user",
    ];

    public id: number;
    public attrs: Album;
    public errors: ErrorMap;
    public statusName: string;
    public metadataFileId: number;
    public bundleFileId: number;
    public albumId: number;
    public userId: number;
    public organisationId: number;
    public createdAt: Date;
    public updatedAt: Date;
    // Relationships
    public status: BundleStatus;
    public metadataFile: File;
    public bundleFile: File;
    public album: Album;
    public user: User;

    public getViewUrl(): string {
        return this.id ? "/bundles/view/" + this.id : null;
    }

    public isStepCreateAlbum(): boolean {
        return this.statusName === "in-progress" || this.statusName === "bundle-updated";
    }

    public isStepUploadZip(): boolean {
        return this.bundleFileId == null && this.statusName === "album-created";
    }

    public isStepComplete(): boolean {
        return this.bundleFileId != null && this.statusName === "zip-uploaded";
    }

    public isStepError(): boolean {
        return this.statusName === "error";
    }
}

export class BundleStatus extends BundleStatusRecord implements IBundleStatus {
    public name: string;
    public label: TranslatableMap;
}

export interface UploadNormalisedEntities extends MusicNormalisedEntities, AuthNormalisedEntities, NormalisedEntities {
    bundles?: Map<number, Bundle>;
    bundleStatuses?: Map<string, BundleStatus>;
}
